import { useAutoAnimate } from '@formkit/auto-animate/react'
import {
  breakpointSizes,
  getBreakpointDown,
  getBreakpointUp,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { Location } from 'history'
import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import SvgMore from 'src/components/Svgs/SvgMore'
import TestId from 'src/components/TestId/TestId'
import { useDropdownOpen } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { LocationState } from 'src/redux/createRootReducer'
import { selectBrandColorCta } from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

export type MoreDropdownOption =
  | MoreDropdownOptionAriaSource
  | MoreDropdownOptionAriaLabel

interface MoreDropdownOptionShared {
  label: React.ReactNode
  image?: React.ReactNode
  to?: string | Location<LocationState>
  onClick?: () => void
  grayed?: boolean
  key?: string | number
  subtitle?: React.ReactNode
  id?: string
  testId?: string
}

interface MoreDropdownOptionAriaSource extends MoreDropdownOptionShared {
  ariaLabelSource: string
  ariaLabel?: undefined
}

interface MoreDropdownOptionAriaLabel extends MoreDropdownOptionShared {
  ariaLabel: string
  ariaLabelSource?: undefined
}

export enum MoreDropdownDesigns {
  Topnav = 'topnav',
  CtaBlueWhiteBg = 'cta-blue-white-bg',
  WhiteToCta = 'white-to-cta',
  WhiteToGray = 'white-to-gray',
  CtaToWhite = 'cta-to-white',
}

export interface MoreDropdownProps {
  id?: string
  className?: string
  design: MoreDropdownDesigns
  topOptions: MoreDropdownOption[]
  bottomOptions: MoreDropdownOption[]
  descriptionTextComponent?: React.ReactElement
  openComponent?: React.ReactElement
  closedComponent?: React.ReactElement
  disabled?: boolean
  width?: string
  hover?: boolean
  fillIcon?: boolean
  openAbove?: boolean
  verticalBuffer?: number
  alignRight?: boolean
  minWidth?: number
  dropdownOpenDebug?: boolean // this is purely for debugging purposes and should never be set
  bottomOptionsCtaStyle?: boolean
}

const MoreDropdown: React.FC<MoreDropdownProps> = (props) => {
  const translate = useTranslate()
  const ctaColor = useSelector(selectBrandColorCta)

  const [dropdownRef] = useAutoAnimate({ duration: 100, easing: 'ease-in' })

  const { dropdownOpen, divRef, handleOpenerClick, setDropdownOpen } =
    useDropdownOpen({
      closeOnSecondClick: true,
      closeOnAnyClick: true,
    })
  const ref: React.MutableRefObject<HTMLDivElement | null> = useRef(null)

  const onClick = !props.disabled ? handleOpenerClick : undefined

  const fillIconCss =
    (dropdownOpen || props.dropdownOpenDebug) &&
    !!props.fillIcon &&
    css`
      svg g,
      svg path {
        fill: ${styleConstants.colors.white};
      }
    `

  const dropdownDesign = (design: MoreDropdownDesigns): string => {
    switch (design) {
      case MoreDropdownDesigns.Topnav: {
        return `
          background-color: ${styleConstants.colors.white};
          transition: box-shadow 150ms ease-out;

          ${getBreakpointUp(breakpointSizes.SM)(css`
            box-shadow:
              0 2px 5px 0 rgba(0, 0, 0, 0.15),
              inset 0px 0px 0px 1px ${styleConstants.colors.gray_medium};

            &:hover {
              box-shadow:
                0 1px 2px 0 rgba(0, 0, 0, 0.15),
                inset 0px 0px 0px 1px ${styleConstants.colors.gray_medium};
            }
          `)}

          ${fillIconCss}
        `
      }
      case MoreDropdownDesigns.WhiteToCta: {
        return `
          transition: background-color 150ms ease-out;
          svg g,
          svg path {
            fill: ${styleConstants.colors.white};
            transition: fill 150ms ease-out;
          }

          ${fillIconCss}
        `
      }
      case MoreDropdownDesigns.WhiteToGray:
      case MoreDropdownDesigns.CtaToWhite: {
        return `
          transition: background-color 150ms ease-out;
          svg g,
          svg path {
            transition: fill 150ms ease-out;
          }

          ${fillIconCss}
        `
      }
      case MoreDropdownDesigns.CtaBlueWhiteBg: {
        return `
          transition: background-color 150ms ease-out;
          svg g,
          svg path {
            transition: fill 150ms ease-out;
            fill: ${ctaColor};
          }

          ${fillIconCss}
        `
      }
    }
  }

  const [baseComponentHover, setBaseComponentHover] = useState(false)
  const [menuComponentHover, setMenuComponentHover] = useState(false)

  useEffect(() => {
    if (!baseComponentHover && !menuComponentHover) {
      setDropdownOpen(false)
    }
  }, [baseComponentHover, menuComponentHover])

  return (
    <TestId testId={'moreDropdown'}>
      <div
        data-isopen={dropdownOpen}
        id={props.id}
        className={props.className}
        css={css`
          position: relative;
          display: flex;
          align-items: flex-end;
          justify-content: center;
          flex-direction: column;
          ${getBreakpointDown(breakpointSizes.XSM)(css`
            ${props.design === MoreDropdownDesigns.Topnav &&
            props.topOptions.length + props.bottomOptions.length === 0 &&
            `display: none;`}
          `)}
          ${getBreakpointUp(breakpointSizes.XSM)(css`
            ${props.design === MoreDropdownDesigns.Topnav &&
            props.topOptions.length < 2 &&
            props.bottomOptions.length === 0 &&
            `display: none;`}
          `)}
          ${props.design === MoreDropdownDesigns.WhiteToGray &&
          dropdownOpen &&
          css`
            background-color: ${styleConstants.colors.gray_light};
          `}
        `}
        ref={divRef}
        onMouseEnter={(): void => {
          if (!props.disabled) {
            setDropdownOpen(true)
            setBaseComponentHover(true)
          }
        }}
        onMouseLeave={(): void => {
          if (!props.disabled) {
            setBaseComponentHover(false)
          }
        }}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            ${props.openAbove &&
            css`
              padding-top: 0px;
            `}
          `}
          onClick={onClick}
          onKeyPress={(): void => {
            return
          }}
          role="button"
          aria-label={translate('more dropdown')}
          tabIndex={0}
          ref={ref}
        >
          {props.descriptionTextComponent}
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: center;
              border-radius: 10px;
              cursor: pointer;
              ${props.openAbove &&
              dropdownOpen &&
              css`
                position: absolute;
                top: 0;
                left: 0;
                height: 100%;
                width: 100%;
                background-color: ${styleConstants.colors.gray_medium};
                border-radius: 8px;
              `}
              ${dropdownDesign(props.design)}
              ${dropdownOpen &&
              css`
                background-color: ${styleConstants.colors.gray_medium};
              `}
              ${props.disabled &&
              css`
                cursor: default;
                opacity: 60%;
              `}
              ${!!props.closedComponent &&
              css`
                height: 25px;
                width: 25px;
              `}
              ${!!props.openComponent &&
              css`
                height: 25px;
                width: 25px;
              `}
              ${!!(props.openComponent && props.closedComponent) &&
              css`
                justify-content: flex-end;
              `}
            `}
          >
            {dropdownOpen || props.dropdownOpenDebug
              ? props.openComponent || <SvgMore />
              : props.closedComponent || <SvgMore />}
          </div>
        </div>
        {props.openAbove && dropdownOpen && (
          <div
            css={css`
              position: absolute;
              height: 40px;
              width: 100%;
              top: -40px;
              left: 0;
              background: transparent;
              z-index: 0;
            `}
            onMouseEnter={(): void => {
              setMenuComponentHover(true)
            }}
            onMouseLeave={(): void => {
              setMenuComponentHover(false)
            }}
          />
        )}
        <div ref={dropdownRef}>
          {(dropdownOpen || props.dropdownOpenDebug) && (
            <div
              css={css`
                z-index: 1;
                position: absolute;
                right: 0px;
                ${props.openAbove
                  ? props.verticalBuffer
                    ? css`
                        bottom: ${props.verticalBuffer}px;
                      `
                    : css`
                        bottom: 45px;
                      `
                  : props.verticalBuffer
                    ? css`
                        top: ${props.verticalBuffer}px;
                      `
                    : css``}
                ${props.alignRight
                  ? css`
                      left: -30px;
                    `
                  : css``}
                display: flex;
                flex-direction: column;
                min-width: ${css`
                  ${props.minWidth}px
                ` || css`250px`};
                ${props.width &&
                css`
                  width: ${props.width};
                `}
                border-radius: ${styleConstants.borderRadius.box};
                border: 1px solid ${styleConstants.colors.gray_medium};
                box-shadow: 0 3px 10px 0 rgba(0, 0, 0, 0.15);

                ${getBreakpointDown(breakpointSizes.XSM)(css`
                  width: auto;
                `)}
              `}
              onClick={(e): void => {
                e.preventDefault()
              }}
              onKeyPress={(e): void => {
                e.preventDefault()
              }}
              role={'button'}
              tabIndex={0}
              onMouseEnter={(): void => {
                setMenuComponentHover(true)
              }}
              onMouseLeave={(): void => {
                setMenuComponentHover(false)
              }}
            >
              {/* invisible hitbox */}
              {!props.openAbove && ref.current?.clientHeight && (
                <div
                  css={css`
                    height: ${ref.current?.clientHeight}px;
                    opacity: 0;
                    bottom: 100%;
                    width: 100%;
                    z-index: 1;
                    position: absolute;
                    cursor: pointer;
                  `}
                  onMouseEnter={(): void => setMenuComponentHover(true)}
                  onMouseLeave={(): void => setMenuComponentHover(false)}
                />
              )}
              {props.topOptions.length > 0 && (
                <div
                  css={css`
                    background-color: ${styleConstants.colors.white};
                    border-bottom: 1px solid ${styleConstants.colors.gray_light};
                    border-radius: ${props.bottomOptions.length === 0
                      ? styleConstants.borderRadius.box
                      : `${styleConstants.borderRadius.box} ${styleConstants.borderRadius.box} 0 0`};

                    ${props.bottomOptions.length === 0 &&
                    css`
                      border-bottom: none;
                    `}

                    ${getBreakpointUp(breakpointSizes.SM)(css`
                      ${props.topOptions.length === 1 &&
                      props.design === MoreDropdownDesigns.Topnav &&
                      css`
                        display: none;
                      `}
                    `)}

                  cursor: default;
                  `}
                >
                  {props.topOptions.map((option, i) => {
                    return (
                      <div
                        key={option.key || i}
                        css={css`
                          padding: 12px 20px;
                          &:first-child {
                            padding: 16px 20px 12px 20px;
                          }
                          &:last-child {
                            border-bottom: 1px solid
                              ${styleConstants.colors.gray_medium};
                          }
                          &:hover {
                            background-color: ${styleConstants.colors
                              .gray_light};
                          }
                          ${i === 0 &&
                          css`
                            border-top-left-radius: ${styleConstants
                              .borderRadius.box};
                            border-top-right-radius: ${styleConstants
                              .borderRadius.box};
                          `}
                          ${i === props.topOptions.length - 1 &&
                          props.bottomOptions === undefined &&
                          css`
                            border-bottom-left-radius: ${styleConstants
                              .borderRadius.box};
                            border-bottom-right-radius: ${styleConstants
                              .borderRadius.box};
                          `}
                          ${!!option.grayed &&
                          css`
                            pointer-events: none;
                          `}
                        `}
                        onClick={option.onClick}
                        onKeyDown={option.onClick}
                        role="button"
                        tabIndex={0}
                      >
                        <HoverableLink
                          id={option.id}
                          testId={option.testId}
                          key={i}
                          to={option.to}
                          css={css`
                            display: flex;
                            align-items: center;
                            align-self: center;
                            ${getFontCss(
                              14,
                              themeConstants.colors.cta,
                              styleConstants.fontFamilies.medium,
                            )};
                            white-space: normal;

                            ${!!option.grayed &&
                            css`
                              opacity: 0.5;
                              cursor: default;
                              pointer-events: none;
                            `}

                            ${props.design === MoreDropdownDesigns.Topnav &&
                            getBreakpointUp(breakpointSizes.SM)(css`
                              &:first-child {
                                display: none;
                              }
                            `)}
                          `}
                          {...(option.ariaLabelSource !== undefined
                            ? { ariaLabelSource: option.ariaLabelSource }
                            : { ariaLabel: option.ariaLabel })}
                        >
                          {option.image && (
                            <div
                              css={css`
                                margin-right: 5px;
                                ${!option.subtitle &&
                                `
                                margin-top: 5px;
                              `}
                              `}
                            >
                              {option.image}
                            </div>
                          )}
                          {option.label}
                        </HoverableLink>
                        {option.subtitle !== undefined && option.subtitle}
                      </div>
                    )
                  })}
                </div>
              )}
              {props.bottomOptions.length > 0 && (
                <div
                  css={css`
                    background-color: ${styleConstants.colors.white};
                    border-radius: ${props.topOptions.length === 0
                      ? styleConstants.borderRadius.box
                      : `0 0 ${styleConstants.borderRadius.box} ${styleConstants.borderRadius.box}`};

                    ${getBreakpointUp(breakpointSizes.SM)(css`
                      ${props.topOptions.length === 1 &&
                      props.design === MoreDropdownDesigns.Topnav &&
                      css`
                        padding-bottom: 0px;
                      `}
                    `)}
                  `}
                >
                  {props.bottomOptions.map((option, i) => (
                    <div
                      key={i}
                      css={css`
                        padding: 12px 20px;
                        &:last-child {
                          padding: 12px 20px 16px 20px;
                        }
                        &:not(:last-child) {
                          border-bottom: 1px solid
                            ${styleConstants.colors.gray_medium};
                        }
                        &:hover {
                          background-color: ${styleConstants.colors.gray_light};
                        }
                        ${i === 0 &&
                        css`
                          border-top-left-radius: ${styleConstants.borderRadius
                            .box};
                          border-top-right-radius: ${styleConstants.borderRadius
                            .box};
                        `}
                        ${i === props.topOptions.length - 1 &&
                        css`
                          border-bottom-left-radius: ${styleConstants
                            .borderRadius.box};
                          border-bottom-right-radius: ${styleConstants
                            .borderRadius.box};
                        `}
                      `}
                      onClick={option.onClick}
                      onKeyDown={option.onClick}
                      role="button"
                      tabIndex={0}
                    >
                      <HoverableLink
                        id={option.id}
                        testId={option.testId}
                        key={i}
                        to={option.to}
                        css={css`
                          display: flex;
                          align-items: center;
                          align-self: center;
                          ${getFontCss(
                            14,
                            props.bottomOptionsCtaStyle
                              ? themeConstants.colors.cta
                              : styleConstants.colors.red,
                            styleConstants.fontFamilies.medium,
                          )};
                          white-space: normal;

                          :hover {
                            ${getFontCss(
                              14,
                              props.bottomOptionsCtaStyle
                                ? themeConstants.colors.cta_dark
                                : styleConstants.colors.red_dark,
                              styleConstants.fontFamilies.medium,
                            )};
                          }
                        `}
                        {...(option.ariaLabelSource !== undefined
                          ? { ariaLabelSource: option.ariaLabelSource }
                          : { ariaLabel: option.ariaLabel })}
                      >
                        {option.image && (
                          <div
                            css={css`
                              margin-right: 5px;
                              ${!option.subtitle &&
                              `
                              margin-top: 5px;
                            `}
                            `}
                          >
                            {option.image}
                          </div>
                        )}
                        {option.label}
                      </HoverableLink>
                      {option.subtitle !== undefined && option.subtitle}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </TestId>
  )
}

export default MoreDropdown
